import { useState } from "react";
import { useEditorStore } from "@/store/editorStore";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Globe, Copy, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function PublishDrawer({ open, onClose }) {
  const projectId = useEditorStore((s) => s.projectId);
  const projectName = useEditorStore((s) => s.projectName);
  const pages = useEditorStore((s) => s.pages);
  const [publishing, setPublishing] = useState(false);
  const [result, setResult] = useState(null);

  const publish = async () => {
    setPublishing(true);
    try {
      await api.patch(`/projects/${projectId}`, { name: projectName, pages });
      const { data } = await api.post(`/projects/${projectId}/publish`);
      setResult(data);
      toast.success("Project published");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setPublishing(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:max-w-[400px]" data-testid="publish-drawer">
        <SheetHeader>
          <SheetTitle className="font-display tracking-tight text-2xl">Publish to web</SheetTitle>
          <SheetDescription>Generate a live, hosted URL for your project in one click.</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="rounded-lg border border-gray-200 p-4 bg-gray-50">
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Project</div>
            <div className="font-display font-semibold text-base">{projectName}</div>
            <div className="text-xs text-gray-500">{pages.length} page{pages.length === 1 ? "" : "s"}</div>
          </div>

          {!result ? (
            <Button
              className="w-full bg-[#002FA7] hover:bg-blue-800 text-white h-11"
              onClick={publish}
              disabled={publishing}
              data-testid="publish-confirm-button"
            >
              {publishing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating live URL…</>
              ) : (
                <><Globe className="w-4 h-4 mr-2" /> Publish website now</>
              )}
            </Button>
          ) : (
            <div className="space-y-3" data-testid="publish-result">
              <div className="text-sm font-semibold text-emerald-700">Your site is live</div>
              <div className="flex items-center gap-2">
                <Input value={result.url} readOnly className="text-xs font-mono" data-testid="published-url-input" />
                <Button
                  variant="outline"
                  className="px-3"
                  onClick={() => {
                    navigator.clipboard.writeText(result.url);
                    toast.success("Link copied");
                  }}
                  data-testid="publish-copy-button"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <a
                href={result.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-[#002FA7] hover:underline"
                data-testid="published-open-link"
              >
                Open live site <ExternalLink className="w-3.5 h-3.5" />
              </a>
            </div>
          )}

          <p className="text-xs text-gray-500 leading-relaxed">
            Layout is auto-optimized for Desktop, Tablet, and Mobile viewports.
            Re-publishing updates the same URL.
          </p>
        </div>
      </SheetContent>
    </Sheet>
  );
}
